import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React, { useCallback, useMemo } from "react";

import { Input } from "@replo/design-system/components/input/Input";
import {
  getOptimisticValue,
  useIsProcessing,
  useLastProcessedRequest,
  useQueue,
} from "@v2/features/modifiers/store/ModifierQueueStoreContext";
import { useSelectedElementDesignations } from "@v2/features/modifiers/store/ModifiersStoreContext";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

interface LinkModifierProps {
  onChange: (modifiers: UniversalModifierValue[]) => void;
}

const LinkModifier: React.FC<LinkModifierProps> = ({ onChange }) => {
  const selectedElementDesignations = useSelectedElementDesignations();
  const linkDesignation = selectedElementDesignations?.find(
    (designation) => designation.type === "link",
  );

  const queue = useQueue();
  const lastProcessedRequest = useLastProcessedRequest();
  const isProcessing = useIsProcessing();

  const stableElementId =
    linkDesignation?.type === "link"
      ? linkDesignation.element.stableElementId
      : null;

  /**
   * NOTE (Kurt, 2025-12-12): Get current href value. Checks the optimistic value from the
   * modifier queue first, then falls back to the link designation value from the server.
   */
  const currentHref = useMemo(() => {
    if (!stableElementId) {
      return linkDesignation?.href ?? "";
    }

    const optimisticModifier = getOptimisticValue({
      queue,
      lastProcessedRequest,
      isProcessing,
      stableElementId,
      modifierType: { type: "href", value: "" },
    });

    if (optimisticModifier && optimisticModifier.type === "href") {
      return optimisticModifier.value;
    }

    return linkDesignation?.href ?? "";
  }, [
    stableElementId,
    queue,
    lastProcessedRequest,
    isProcessing,
    linkDesignation?.href,
  ]);

  const [hrefValue, setHrefValue] = useOverridableState(currentHref);

  const debouncedOnChange = useDebouncedCallback(onChange, 300);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newHref = e.target.value;
      setHrefValue(newHref);
      debouncedOnChange([
        {
          type: "href",
          value: newHref,
        },
      ]);
    },
    [setHrefValue, debouncedOnChange],
  );

  const handleBlur = useCallback(() => {
    debouncedOnChange.flush();
  }, [debouncedOnChange]);

  if (!linkDesignation) {
    return null;
  }

  return (
    <Input
      placeholder="Enter URL (e.g., https://example.com)"
      value={hrefValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default LinkModifier;
